import { Box, Button, Flex, Text } from '@theme-ui/components'
import { ReportAPI } from 'api'
import { Formik, FormikHelpers } from 'formik'
import { getOr, pick, pipe } from 'lodash/fp'
import { createContext, ReactNode } from 'react'
import {
  BsCalendarEvent,
  BsChat,
  BsFillFileEarmarkTextFill,
  BsFillQuestionCircleFill,
} from 'react-icons/bs'
import {
  FaBan,
  FaFilter,
  FaInfo,
  FaPaperPlane,
  FaUserFriends,
} from 'react-icons/fa'
import { useQuery } from 'react-query'
import { Link } from 'react-router-dom'
import { array, boolean, object, string } from 'yup'
import { getSelection } from '../../../api/reportFunction'
import FormikSelect from '../../../components/Formik/FormikSelect'
import { notifyError } from '../../../components/Notif'
import { PanelComponent } from '../../../components/Panel/Panel'
import Attachments from './Attachments'
import InvolvedParties from './InvolvedParties'
import { Terms } from './Notes'
import ReportLine from './ReportLine'
import TimeOfEvents from './TimeOfEvents'
import ViolationRadio from './ViolationRadio'
import useCurrentUserStore from '../../../stores/currentUser'
import ReportNotes from './ReportNotes'
import { match, when, __ } from 'ts-pattern'
import { matchRegExp } from '../../../utils'
import FormikRichTextEditor from '../../../components/Formik/FormikRichTextEditor'

export const ReportContext = createContext<ReportContextType>({
  state: 'editing',
  listDivisi: [],
  listPengaduan: [],
  report: null,
})

interface ReportContextType {
  state: 'editing' | 'readonly'
  report: ReportAPI.Data
  [k: string]: any
}

export interface ReportLineType<ValueType = string> {
  id?: any
  reportId?: number
  fieldName?: string
  valueName?: ValueType
  notes?: string
  isChecklist?: boolean
  fieldType?: string
  createdAt?: string
  attachments?: Array<any> | undefined | null
}

export type Suspect = {
  id?: string
  namaPersonel: string
  unitDivisi: string
  proven?: boolean
}

export interface ReportFormValues {
  id?: number
  reportType?: ReportLineType
  whatHappened?: ReportLineType
  channel?: string
  involvedParties?: ReportLineType<Array<Suspect>>
  timeOfEvents?: ReportLineType<
    Array<{ id?: string; tanggal: string; keterangan: string }>
  >
  attachments?: ReportLineType<
    Array<{
      id?: string
      urlPath: string
      fileName?: string
      jenisFile?: string
      createdAt?: string
      size?: number
      notes: string
    }>
  >
  deleteAttachmentByIds?: Array<string | number>
  isViolation?: boolean
  finalNotes?: string
}

export interface ReportFormProps extends Partial<ReportContextType> {
  initialValues: ReportFormValues
  disabled?: boolean
  onSubmit(
    values: ReportFormValues,
    formikHelpers: FormikHelpers<ReportFormValues>
  ): void
  renderHeader?(
    values: ReportFormValues,
    validateForm: FormikHelpers<ReportFormValues>['validateForm']
  )
  renderActions?(
    values: ReportFormValues,
    validateForm: FormikHelpers<ReportFormValues>['validateForm']
  ): ReactNode
}

// =============================================================================
// Form
// =============================================================================

export default function ReportForm({
  onSubmit,
  initialValues,
  disabled,
  state = 'editing',
  report,
  renderHeader,
  renderActions,
}: ReportFormProps) {
  const roles = useCurrentUserStore((s) => s.currentUser?.data?.roles)
  const selectionQ = useQuery('/selections', getSelection)
  const { listDivisi = [], listPengaduan = [] } = pipe(
    getOr({}, 'data'),
    pick(['listDivisi', 'listPengaduan']),
    (data): any => ({ ...data })
  )(selectionQ?.data as any)

  const validationSchema = object({
    reportType: object({ valueName: string().defined('Wajib diisi') }),
    whatHappened: object({ valueName: string().defined('Wajib diisi') }),
    involvedParties: object({
      valueName: array()
        // .test({
        //   name: 'sanksi',
        //   message: 'Sanksi wajib diisi',
        //   test: (suspects) => {
        //     let valid = true
        //     if (
        //       !matchRegExp(/admin_reviewer|admin_board_of_honor/)(roles) &&
        //       report?.state !== 'verdict'
        //     )
        //       return true

        //     console.log({ valid })
        //     ;(suspects || []).forEach((sus) => {
        //       if (sus.proven && (!sus.fileUrl || !sus.penalty)) valid = false
        //     })
        //     return valid
        //   },
        // })
        .min(1, 'Wajib diisi')
        .required('Wajib diisi'),
    }),
    // timeOfEvents: object({
    //   valueName: array().min(1, 'Wajib diisi').required('Wajib diisi'),
    // }),
    attachments: object({
      attachments: array().min(1, 'Wajib diisi').required('Wajib diisi'),
    }),
    ...(report?.state === 'unverified' && {
      isViolation: boolean().defined(
        'Wajib diisi, Apakah laporan ini merupakan pelanggaran?'
      ),
    }),
  })

  const reportTypeDisabled = match([roles, report?.state])
    .with(['admin_verificator', 'reported'], () => false)
    .otherwise(() => state === 'readonly')

  return (
    <ReportContext.Provider
      value={{ listDivisi, listPengaduan, state, report }}
    >
      <Formik<ReportFormValues>
        initialValues={{
          reportType: { valueName: '' },
          whatHappened: {
            valueName: '<br /><br /><br /><br /><br /><br /><br /><br />',
          },
          involvedParties: { valueName: [] },
          timeOfEvents: { valueName: [] },
          attachments: { attachments: [] },
          deleteAttachmentByIds: [],
          ...initialValues,
        }}
        enableReinitialize
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ errors, values, validateForm, submitForm }) => {
          function clickSubmit() {
            validateForm(values).then((formErrors) => {
              const errorKeys = Object.keys(formErrors)
              const selectors = errorKeys.map((key) => `#${key}`).join(',')

              if (selectors) return scrollToViewAndAlert(selectors)
              if (
                values.attachments.attachments.reduce(
                  (sum, file) => sum + file.size,
                  0
                ) > 104857600
              )
                return notifyError(
                  'Oops, total file melebihi 100MB! Kurangi dokumen pendukung'
                )

              submitForm()
            })
          }

          return (
            <Box>
              {renderHeader?.(values, validateForm)}
              <ReportNotes report={report} />
              <PanelComponent
                header="Apa Yang Terjadi?"
                childPadding={[2, null]}
              >
                <ViolationRadio />
                <ReportLine name="reportType" icon={<FaFilter size={25} />}>
                  <FormikSelect
                    row
                    id="reportType"
                    name="reportType.valueName"
                    label="Jenis Pengaduan"
                    placeholder="ABC"
                    disabled={reportTypeDisabled}
                  >
                    <option value=""></option>
                    {listPengaduan.map((data) => {
                      return (
                        <option key={data.code} value={data.code}>
                          {data.value}
                        </option>
                      )
                    })}
                  </FormikSelect>
                </ReportLine>

                <ReportLine
                  name="whatHappened"
                  icon={<BsFillQuestionCircleFill size={20} />}
                >
                  <Flex sx={{ width: '100%' }}>
                    <Text sx={{ flex: 1 }}>Apa yang terjadi?</Text>
                    <Box sx={{ flex: 4.3 }}>
                      <FormikRichTextEditor
                        id="whatHappened"
                        name="whatHappened.valueName"
                        readOnly={state === 'readonly'}
                      />
                    </Box>
                  </Flex>
                  {/* <FormikTextArea
                    row
                    id="whatHappened"
                    name="whatHappened.valueName"
                    label="Apa Yang Terjadi?"
                    placeholder="Apa Yang Terjadi"
                    errorMessage={errors.whatHappened?.valueName}
                    disabled={state === 'readonly'}
                  /> */}
                </ReportLine>

                {roles === 'admin_verificator' && (
                  <ReportLine name="channel" icon={<BsChat size={20} />}>
                    <FormikSelect
                      row
                      id="channel"
                      name="channel"
                      label="Kanal laporan"
                      // errorMessage={errors.whatHappened?.valueName}
                      disabled={state === 'readonly'}
                    >
                      <option value="web">
                        Aplikasi WBS BPKH pada situs BPKH
                      </option>
                      <option value="sms">layanan pesan singkat/SMS</option>
                      <option value="email">surat elektronik</option>
                      <option value="telephone">telepon</option>
                      <option value="mail">surat</option>
                      <option value="complaint_box">kotak pengaduan</option>
                    </FormikSelect>
                  </ReportLine>
                )}

                {match([roles, report?.state, report?.violation])
                  .with(
                    [
                      when(
                        matchRegExp(
                          /admin_reviewer|admin_board_of_honor|admin_investigator/
                        )
                      ),
                      'verdict',
                      true,
                    ],
                    () => (
                      <Flex mb={4}>
                        <Text sx={{ flex: 1 }}>
                          <Box as={FaInfo} mr={2} /> Catatan dari investigator
                        </Text>
                        <Box
                          sx={{
                            flex: 3.5,
                            border: '1px solid',
                            borderColor: 'primary',
                            p: 3,
                          }}
                          dangerouslySetInnerHTML={{
                            __html: values.finalNotes,
                          }}
                        />
                      </Flex>
                    )
                  )
                  .otherwise(() => null)}

                <ReportLine
                  name="involvedParties"
                  icon={<FaUserFriends size={20} />}
                >
                  <InvolvedParties />
                </ReportLine>
                {match([roles, report?.state])
                  .with(
                    [
                      when(matchRegExp(/(admin_investigator|superadmin)/)),
                      'reviewed',
                    ],
                    () => (
                      <FormikRichTextEditor
                        id="finalNotes"
                        label={
                          <Text sx={{ mt: 3, fontSize: 3 }}>
                            Kesimpulan Pemeriksaan
                          </Text>
                        }
                        name="finalNotes"
                        placeholder="Kesimpulan hasil pemeriksaan investigator"
                      />
                    )
                  )
                  .with(
                    [
                      when(
                        (role) =>
                          !role || matchRegExp(/(user|admin|newuser)/)(role)
                      ),
                      __,
                    ],
                    () => (
                      <>
                        <ReportLine
                          name="timeOfEvents"
                          icon={<BsCalendarEvent size={25} />}
                        >
                          <TimeOfEvents />
                        </ReportLine>

                        <ReportLine
                          name="attachments"
                          icon={<BsFillFileEarmarkTextFill size={30} />}
                        >
                          <Attachments />
                        </ReportLine>

                        {matchRegExp(/undefined|user|admin_verificator/)(
                          roles
                        ) && <Terms />}
                      </>
                    )
                  )
                  .otherwise(() => null)}
              </PanelComponent>

              <Flex my={4} sx={{ justifyContent: 'end', gap: '2' }}>
                {match([report?.state, roles])
                  .when(
                    ([a, b]) => {
                      if (b === 'admin_verificator') return a === 'reported'
                      return a === undefined
                    },
                    () => {
                      return (
                        <>
                          <Link to="/">
                            <Button
                              variant="secondary"
                              type="button"
                              sx={{ width: 130, mr: 3 }}
                            >
                              <FaBan />
                              <Text ml={3}>Batal</Text>
                            </Button>
                          </Link>
                          <Button
                            sx={{ width: 130 }}
                            type="button"
                            disabled={disabled}
                            onClick={clickSubmit}
                          >
                            <FaPaperPlane />
                            <Text ml={3}>Simpan</Text>
                          </Button>
                        </>
                      )
                    }
                  )
                  .otherwise(() => null)}

                {renderActions?.(values, validateForm)}
              </Flex>
            </Box>
          )
        }}
      </Formik>
    </ReportContext.Provider>
  )
}

function scrollToViewAndAlert(selectors = '') {
  if (selectors) {
    const node = document.querySelectorAll(selectors)?.[0]
    node?.scrollIntoView(true)
    const scrolledY = window.scrollY
    if (scrolledY) window.scroll(0, scrolledY - 300)
  }

  notifyError('Mohon lengkapi field')
}
